import React from "react";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  fetchAllApplicantColleges,
  fetchAllCollegesTicker,
  fetchShortlistedCollegesTicker,
  fetchSpotColleges,
  fetchViewerCollegeLogo,
  fetchViewerCollegeRounds,
  mapApiRoundStatus,
  pickDefaultSpotCollege,
  pickDefaultViewerRoundId,
  resolveSpotCollegeItemLogo,
  resolveSpotCollegeViewerId,
  sortMappedSpotRoundsAsc,
  toDateInputValue,
  type SpotCollegeListItem,
  type SpotTickerItem,
  type ViewerRoundOut,
} from "../../../hallticket-lib";
import {
  formatAbs,
  formatDateRange,
  getCollege,
  loadSpotRoundData,
  loadSpotShortlistNames,
  saveSpotShortlistNames,
  statusPillClass,
} from "./spotRoundData";
import { httpClient } from "../../../hallticket-lib/http/client";
import type { SpotAdmissionRound, SpotCollege, SpotRoundData } from "./spotRoundTypes";
import {
  detectTickerSeatTotalChanges,
  expireTickerHighlights,
  isTickerHighlightActive,
  isTickerSeatsRecentlyUpdated,
  markTickerSeatHighlight,
  tickerHighlightKey,
  type TickerSeatHighlightMeta,
} from "./seatChangeHighlight";
import { SpotRoundSeatMatrix } from "./SpotRoundSeatMatrix";
import { useSpotRoundAutoRefresh } from "./useSpotRoundAutoRefresh";
import "./SpotRound.css";

const RECENT_TICKER_SEAT_COLOR = "#ef4444";

type TickerStatus = "idle" | "loading" | "success" | "error";
type TableStatus = "idle" | "loading" | "success" | "error";

function logoInitialFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase() || "HT";
}

function logSelectedHeaderCollege(
  source: string,
  college: SpotCollegeListItem | null,
): void {
  const selectedCollegeId =
    college ? resolveSpotCollegeViewerId(college) ?? college.id ?? null : null;
  // TEMP DEBUG
  console.log(`[SpotRound] ${source} mapped college object`, college);
  console.log(`[SpotRound] ${source} selected collegeId`, selectedCollegeId);
  console.log(`[SpotRound] ${source} selected college name`, college?.name ?? null);
  console.log(`[SpotRound] ${source} selected logo URL`, college?.logoUrl ?? null);
}

function findExporterCollegeByTickerItem(
  colleges: SpotCollegeListItem[],
  item: SpotTickerItem,
): SpotCollegeListItem | undefined {
  const targetName = item.college_name.toLowerCase();
  const targetCode = item.college_code.trim().toLowerCase();

  return colleges.find((college) => {
    const name = college.name.toLowerCase();
    if (name === targetName || name.includes(targetName) || targetName.includes(name)) {
      return true;
    }
    const code = college.collegeCode?.trim().toLowerCase() ?? "";
    if (targetCode && (code === targetCode || college.id === targetCode)) {
      return true;
    }
    return false;
  });
}

function mapViewerRoundToAdmissionRound(
  apiRound: ViewerRoundOut,
  matrixRound?: Pick<SpotAdmissionRound, "matrix" | "courseShortForms">,
): SpotAdmissionRound {
  return {
    id: String(apiRound.id),
    name: apiRound.name?.trim() || `Round ${apiRound.id}`,
    status: mapApiRoundStatus(apiRound.status),
    dayStart: toDateInputValue(apiRound.startDate),
    dayEnd: toDateInputValue(apiRound.endDate),
    notes: "",
    matrix: matrixRound?.matrix ?? { jee: {}, mhtcet: {} },
    courseShortForms: matrixRound?.courseShortForms,
  };
}

function CollegeTickerChip({
  item,
  shortlisted,
  seatsRecentlyUpdated,
  onSelect,
  onToggleShortlist,
}: {
  item: SpotTickerItem;
  shortlisted: boolean;
  seatsRecentlyUpdated: boolean;
  onSelect: (item: SpotTickerItem) => void;
  onToggleShortlist: (item: SpotTickerItem, event: React.MouseEvent) => void;
}) {
  return (
    <div
      className="sr2-chip"
      onClick={() => onSelect(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(item);
        }
      }}
    >
      <button
        type="button"
        className="sr2-star-btn"
        title={shortlisted ? "Remove from shortlist" : "Add to shortlist"}
        onClick={(event) => onToggleShortlist(item, event)}
      >
        {shortlisted ? "★" : "☆"}
      </button>
      <span className="n">{item.college_name}</span>
      <span
        className={`s${seatsRecentlyUpdated ? " sr2-ticker-seats-recent" : ""}`}
        style={seatsRecentlyUpdated ? { color: RECENT_TICKER_SEAT_COLOR } : undefined}
      >
        {item.total_available_seats} seats open
      </span>
    </div>
  );
}

function TickerTrack({
  items,
  shortlistedNames,
  tickerHighlightMap,
  tickerUpdatedOnByCollege,
  now,
  onSelect,
  onToggleShortlist,
}: {
  items: SpotTickerItem[];
  shortlistedNames: string[];
  tickerHighlightMap: Map<string, TickerSeatHighlightMeta>;
  tickerUpdatedOnByCollege: Map<string, number>;
  now: number;
  onSelect: (item: SpotTickerItem) => void;
  onToggleShortlist: (item: SpotTickerItem, event: React.MouseEvent) => void;
}) {
  const renderChips = (keyPrefix: string) =>
    items.map((item, index) => {
      const seatsRecentlyUpdated = isTickerSeatsRecentlyUpdated(
        item,
        tickerHighlightMap,
        tickerUpdatedOnByCollege,
        now,
      );
      return (
        <CollegeTickerChip
          key={`${keyPrefix}-${item.college_code || item.college_name}-${index}`}
          item={item}
          shortlisted={shortlistedNames.some(
            (name) => name.toLowerCase() === item.college_name.toLowerCase(),
          )}
          seatsRecentlyUpdated={seatsRecentlyUpdated}
          onSelect={onSelect}
          onToggleShortlist={onToggleShortlist}
        />
      );
    });

  return (
    <div className="sr2-ticker-wrap">
      <div className="sr2-ticker-track">
        {renderChips("a")}
        {renderChips("b")}
      </div>
    </div>
  );
}

function TickerSection({
  label,
  items,
  status,
  errorMessage,
  emptyMessage,
  shortlistedNames,
  tickerHighlightMap,
  tickerUpdatedOnByCollege,
  now,
  onSelect,
  onToggleShortlist,
}: {
  label: string;
  items: SpotTickerItem[];
  status: TickerStatus;
  errorMessage: string;
  emptyMessage: string;
  shortlistedNames: string[];
  tickerHighlightMap: Map<string, TickerSeatHighlightMeta>;
  tickerUpdatedOnByCollege: Map<string, number>;
  now: number;
  onSelect: (item: SpotTickerItem) => void;
  onToggleShortlist: (item: SpotTickerItem, event: React.MouseEvent) => void;
}) {
  return (
    <>
      <div className="sr2-ticker-label">{label}</div>
      {status === "loading" && (
        <div className="sr2-ticker-state">
          <ProgressSpinner style={{ width: "1.8rem", height: "1.8rem" }} strokeWidth="6" animationDuration=".9s" />
          <span>Loading ticker...</span>
        </div>
      )}
      {status === "error" && (
        <div className="sr2-ticker-state sr2-ticker-state--error">{errorMessage}</div>
      )}
      {status === "success" && items.length === 0 && (
        <div className="sr2-ticker-state">{emptyMessage}</div>
      )}
      {status === "success" && items.length > 0 && (
        <TickerTrack
          items={items}
          shortlistedNames={shortlistedNames}
          tickerHighlightMap={tickerHighlightMap}
          tickerUpdatedOnByCollege={tickerUpdatedOnByCollege}
          now={now}
          onSelect={onSelect}
          onToggleShortlist={onToggleShortlist}
        />
      )}
    </>
  );
}

function buildTickerTotalsMap(items: SpotTickerItem[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(tickerHighlightKey(item), item.total_available_seats);
  }
  return map;
}

function findSeedCollegeByTickerItem(
  colleges: SpotCollege[],
  item: SpotTickerItem,
): SpotCollege | undefined {
  const targetName = item.college_name.toLowerCase();
  const targetCode = item.college_code.trim().toLowerCase();

  return colleges.find((college) => {
    const name = college.name.toLowerCase();
    const shortName = college.shortName.toLowerCase();
    if (name === targetName || name.includes(targetName) || targetName.includes(name)) {
      return true;
    }
    if (targetCode && (shortName === targetCode || college.id.toLowerCase() === targetCode)) {
      return true;
    }
    return false;
  });
}

export function SpotRound() {
  const [data] = React.useState<SpotRoundData>(() => loadSpotRoundData());
  const [shortlistedNames, setShortlistedNames] = React.useState<string[]>(() => loadSpotShortlistNames());
  const [activeCollegeId, setActiveCollegeId] = React.useState<string>(() => data.colleges[0]?.id ?? "");
  const [activeViewerCollegeId, setActiveViewerCollegeId] = React.useState<string>("");
  /** Bumped on every ticker click so same-college re-clicks still reload rounds/spots. */
  const [viewerCollegeReloadNonce, setViewerCollegeReloadNonce] = React.useState(0);
  const [now, setNow] = React.useState(() => Date.now());

  const [exporterColleges, setExporterColleges] = React.useState<SpotCollegeListItem[]>([]);
  const [headerCollege, setHeaderCollege] = React.useState<SpotCollegeListItem | null>(null);

  const [allTickerItems, setAllTickerItems] = React.useState<SpotTickerItem[]>([]);
  const [allTickerStatus, setAllTickerStatus] = React.useState<TickerStatus>("idle");
  const [allTickerError, setAllTickerError] = React.useState("");

  const [shortlistTickerItems, setShortlistTickerItems] = React.useState<SpotTickerItem[]>([]);
  const [shortlistTickerStatus, setShortlistTickerStatus] = React.useState<TickerStatus>("idle");
  const [shortlistTickerError, setShortlistTickerError] = React.useState("");

  const [apiRounds, setApiRounds] = React.useState<SpotAdmissionRound[]>([]);
  const [roundsLoadedCollegeId, setRoundsLoadedCollegeId] = React.useState<string>("");
  const [loadingRounds, setLoadingRounds] = React.useState<boolean>(false);
  const [activeRoundId, setActiveRoundId] = React.useState<string>("");
  const [activeRound, setActiveRound] = React.useState<SpotAdmissionRound | undefined>(undefined);
  const [tableStatus, setTableStatus] = React.useState<TableStatus>("idle");
  const [tableError, setTableError] = React.useState("");
  const [tickerHighlightMap, setTickerHighlightMap] = React.useState<
    Map<string, TickerSeatHighlightMeta>
  >(() => new Map());
  const [tickerUpdatedOnByCollege, setTickerUpdatedOnByCollege] = React.useState<
    Map<string, number>
  >(() => new Map());
  const apiRoundsRef = React.useRef(apiRounds);
  apiRoundsRef.current = apiRounds;
  const activeViewerCollegeIdRef = React.useRef(activeViewerCollegeId);
  activeViewerCollegeIdRef.current = activeViewerCollegeId;
  const allTickerItemsRef = React.useRef(allTickerItems);
  allTickerItemsRef.current = allTickerItems;
  const shortlistTickerItemsRef = React.useRef(shortlistTickerItems);
  shortlistTickerItemsRef.current = shortlistTickerItems;
  const colleges = data.colleges;
  const activeCollege = getCollege(data, activeCollegeId) ?? colleges[0];

  const activeRoundStatus = apiRounds.find((round) => round.id === activeRoundId)?.status
    ?? activeRound?.status;

  const handleSeatMatrixChanged = React.useCallback(
    ({ changedKeys, detectedAt }: { changedKeys: string[]; detectedAt: number }) => {
      const collegeId = activeViewerCollegeIdRef.current;
      if (!collegeId || changedKeys.length === 0) {
        return;
      }

      const tickerItem = [...allTickerItemsRef.current, ...shortlistTickerItemsRef.current].find(
        (item) => item.collegeId === collegeId || tickerHighlightKey(item) === collegeId,
      );

      setTickerHighlightMap((current) => {
        const next = markTickerSeatHighlight(
          current,
          collegeId,
          tickerItem?.total_available_seats ?? 0,
          detectedAt,
        );
        console.log("[SpotRound] ticker highlighted from table seat change", {
          key: collegeId,
          changedCellCount: changedKeys.length,
          tickerUpdateTimestamp: detectedAt,
          tickerRedStateActive: isTickerHighlightActive(next, collegeId, detectedAt),
        });
        return next;
      });
    },
    [],
  );

  const handleSpotUpdatedOnCollected = React.useCallback(
    ({ collegeId, latestUpdatedOnMs }: { collegeId: string; latestUpdatedOnMs: number | null }) => {
      if (!collegeId) {
        return;
      }

      setTickerUpdatedOnByCollege((current) => {
        const existing = current.get(collegeId);
        if (latestUpdatedOnMs == null) {
          if (existing == null) {
            return current;
          }
          const next = new Map(current);
          next.delete(collegeId);
          console.log("[SpotRound] ticker updatedOn cleared", { key: collegeId });
          return next;
        }
        if (existing === latestUpdatedOnMs) {
          return current;
        }
        const next = new Map(current);
        next.set(collegeId, latestUpdatedOnMs);
        console.log("[SpotRound] ticker updatedOn stored", {
          key: collegeId,
          latestUpdatedOnMs,
        });
        return next;
      });
    },
    [],
  );

  const refreshRoundsSilently = React.useCallback(async () => {
    console.log("[SpotRound] Round API refresh");
    const collegeId = activeViewerCollegeIdRef.current;
    if (!collegeId) {
      return;
    }

    try {
      const roundsResponse = await fetchViewerCollegeRounds(collegeId);
      const mappedRounds = sortMappedSpotRoundsAsc(
        roundsResponse.map((round) => mapViewerRoundToAdmissionRound(round)),
        "[SpotRound][Public]",
      );
      setApiRounds(mappedRounds);
      setActiveRoundId((current) => {
        if (current && mappedRounds.some((round) => round.id === current)) {
          return current;
        }
        return pickDefaultViewerRoundId(roundsResponse) ?? "";
      });
    } catch (error: unknown) {
      console.warn("[SpotRound] Round API refresh failed", error);
    }
  }, []);

  const refreshTickersSilently = React.useCallback(async () => {
    console.log("[SpotRound] Ticker API refresh");
    try {
      const [nextAll, preferredColleges] = await Promise.all([
        fetchAllCollegesTicker(),
        fetchAllApplicantColleges(),
      ]);
      const preferredIds = preferredColleges
        .filter((college) => college.preferred)
        .map((college) => college.collegeId);
      const nextShortlist = preferredIds.length > 0
        ? await fetchShortlistedCollegesTicker(preferredIds)
        : [];

      const previousTotals = buildTickerTotalsMap([
        ...allTickerItemsRef.current,
        ...shortlistTickerItemsRef.current,
      ]);
      const detectedAt = Date.now();
      const mergedNext = [...nextAll, ...nextShortlist];

      setTickerHighlightMap((current) => {
        const { nextHighlights } = detectTickerSeatTotalChanges(
          previousTotals,
          mergedNext,
          detectedAt,
          current,
        );

        for (const item of mergedNext) {
          const key = tickerHighlightKey(item);
          const previousTickerTotal = previousTotals.get(key);
          const newTickerTotal = item.total_available_seats;
          const tickerTotalChanged = previousTickerTotal !== undefined
            && previousTickerTotal !== newTickerTotal;
          console.log("[SpotRound] ticker refresh compare", {
            key,
            collegeName: item.college_name,
            previousTickerTotal: previousTickerTotal ?? null,
            newTickerTotal,
            tickerTotalChanged,
            tickerUpdateTimestamp: tickerTotalChanged ? detectedAt : null,
            tickerRedStateActive: isTickerHighlightActive(nextHighlights, key, detectedAt),
          });
        }

        return nextHighlights;
      });

      setAllTickerItems(nextAll);
      setAllTickerStatus("success");
      setShortlistTickerItems(nextShortlist);
      setShortlistTickerStatus("success");
    } catch (error: unknown) {
      console.warn("[SpotRound] Ticker API refresh failed", error);
    }
  }, []);

  const onAutoRefreshTick = React.useCallback(async () => {
    await Promise.all([refreshRoundsSilently(), refreshTickersSilently()]);
  }, [refreshRoundsSilently, refreshTickersSilently]);

  const { loadRoundSpots, highlightedCellKeys, highlightMetaByKey, lastUpdatedAt } = useSpotRoundAutoRefresh({
    activeRoundId,
    activeRoundStatus,
    activeViewerCollegeId,
    roundsLoadedCollegeId,
    now,
    apiRoundsRef,
    setActiveRound,
    setTableStatus,
    setTableError,
    onAutoRefreshTick,
    onSeatMatrixChanged: handleSeatMatrixChanged,
    onSpotUpdatedOnCollected: handleSpotUpdatedOnCollected,
  });

  const displayCollegeName = headerCollege?.name?.trim() || "";
  const displayCollegeLogoUrl = headerCollege?.logoUrl?.trim() || "";
  const displayCollegeAddress = headerCollege?.location?.trim() || "";
  const displayLogoInitial = logoInitialFromName(displayCollegeName);
  const selectedCollegeId = headerCollege
    ? resolveSpotCollegeViewerId(headerCollege) ?? headerCollege.id ?? null
    : null;

  React.useEffect(() => {
    let cancelled = false;

    async function resolveHeaderCollegeLogo(): Promise<void> {
      if (!headerCollege || !selectedCollegeId) {
        return;
      }

      const collegeId = selectedCollegeId;
      const collegeSnapshot = headerCollege;

      // TEMP DEBUG
      console.log("[SpotRound] selected collegeId", collegeId);
      console.log("[SpotRound] mapped college object", collegeSnapshot);
      console.log("[SpotRound] logoFileId", collegeSnapshot.logoFileId ?? null);
      console.log("[SpotRound] headerCollege.logoUrl (before resolve)", collegeSnapshot.logoUrl ?? null);

      let resolvedLogoUrl = collegeSnapshot.logoUrl?.trim() || "";

      if (!resolvedLogoUrl && collegeSnapshot.logoFileId != null) {
        try {
          const withExporterLogo = await resolveSpotCollegeItemLogo(collegeSnapshot);
          resolvedLogoUrl = withExporterLogo.logoUrl?.trim() || "";
          console.log("[SpotRound] resolved logoUrl (exporter logoFileId)", resolvedLogoUrl || null);
        } catch (error: unknown) {
          console.warn("[SpotRound] exporter logoFileId resolve failed", {
            collegeId,
            logoFileId: collegeSnapshot.logoFileId,
            error,
          });
        }
      }

      if (!resolvedLogoUrl) {
        try {
          const viewerLogo = await fetchViewerCollegeLogo(collegeId);
          resolvedLogoUrl = viewerLogo.logoUrl?.trim() || "";
          console.log("[SpotRound] resolved logoUrl (viewer logo API)", resolvedLogoUrl || null);
        } catch (error: unknown) {
          console.warn("[SpotRound] viewer logo API failed — initials placeholder will show", {
            collegeId,
            error,
          });
        }
      }

      console.log("[SpotRound] final image URL rendered", resolvedLogoUrl || null);

      if (cancelled || !resolvedLogoUrl || resolvedLogoUrl === collegeSnapshot.logoUrl?.trim()) {
        return;
      }

      setHeaderCollege((current) => {
        if (!current) {
          return current;
        }
        const currentCollegeId = resolveSpotCollegeViewerId(current) ?? current.id ?? null;
        if (currentCollegeId !== collegeId) {
          return current;
        }
        return {
          ...current,
          logoUrl: resolvedLogoUrl,
          logoFileId: null,
        };
      });

      setExporterColleges((current) =>
        current.map((item) => {
          const itemCollegeId = resolveSpotCollegeViewerId(item) ?? item.id ?? null;
          if (itemCollegeId !== collegeId) {
            return item;
          }
          return {
            ...item,
            logoUrl: resolvedLogoUrl,
            logoFileId: null,
          };
        }),
      );
    }

    void resolveHeaderCollegeLogo();
    return () => {
      cancelled = true;
    };
  }, [
    selectedCollegeId,
    headerCollege?.id,
    headerCollege?.logoFileId,
    headerCollege?.logoUrl,
    headerCollege,
  ]);

  React.useEffect(() => {
    console.log("[SpotRound] headerCollege.logoUrl", headerCollege?.logoUrl ?? null);
    console.log("[SpotRound] <img src> value", displayCollegeLogoUrl || null);
  }, [displayCollegeLogoUrl, headerCollege?.logoUrl]);

  React.useEffect(() => {
    let cancelled = false;

    const loadSpotCollegesHeader = async () => {
      try {
        const collegesFromApi = await fetchSpotColleges();
        if (cancelled) {
          return;
        }

        console.log("[Hallticket][SpotRound] fetchSpotColleges() loaded", collegesFromApi.length, "colleges", {
          sample: collegesFromApi[0]?.name ?? null,
          sampleLogoUrl: collegesFromApi[0]?.logoUrl ?? null,
        });

        setExporterColleges(collegesFromApi);

        const selected = pickDefaultSpotCollege(collegesFromApi);

        setHeaderCollege(selected);
        const resolvedViewerCollegeId = selected ? resolveSpotCollegeViewerId(selected) : null;
        setRoundsLoadedCollegeId("");
        setActiveViewerCollegeId(resolvedViewerCollegeId ?? "");
        logSelectedHeaderCollege("initial load", selected);
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }
        console.error("[SpotRound] SpotCollegesExporter failed", error);
        setExporterColleges([]);
        setHeaderCollege(null);
        setRoundsLoadedCollegeId("");
        setActiveViewerCollegeId("");
      }
    };

    void loadSpotCollegesHeader();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const loadTickers = async () => {
      setAllTickerStatus("loading");
      setAllTickerError("");
      setShortlistTickerStatus("loading");
      setShortlistTickerError("");

      const allPromise = fetchAllCollegesTicker()
        .then((items) => {
          if (cancelled) {
            return;
          }
          setAllTickerItems(items);
          setAllTickerStatus("success");
        })
        .catch((error: unknown) => {
          if (cancelled) {
            return;
          }
          setAllTickerItems([]);
          setAllTickerStatus("error");
          setAllTickerError(
            error instanceof Error && error.message
              ? error.message
              : "Unable to load all colleges ticker.",
          );
        });

      const shortlistPromise = (async () => {
        try {
          const preferredColleges = await fetchAllApplicantColleges();
          const preferredIds = preferredColleges
            .filter((college) => college.preferred)
            .map((college) => college.collegeId);

          if (preferredIds.length === 0) {
            if (cancelled) {
              return;
            }
            setShortlistTickerItems([]);
            setShortlistTickerStatus("success");
            return;
          }

          const items = await fetchShortlistedCollegesTicker(preferredIds);
          if (cancelled) {
            return;
          }
          setShortlistTickerItems(items);
          setShortlistTickerStatus("success");
        } catch (error: unknown) {
          if (cancelled) {
            return;
          }
          setShortlistTickerItems([]);
          setShortlistTickerStatus("error");
          setShortlistTickerError(
            error instanceof Error && error.message
              ? error.message
              : "Unable to load shortlisted colleges ticker.",
          );
        }
      })();

      await Promise.all([allPromise, shortlistPromise]);
    };

    void loadTickers();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const loadRounds = async () => {
      if (!activeViewerCollegeId) {
        setLoadingRounds(false);
        console.log("[SpotRound] loadingRounds = false");
        setTableStatus("success");
        setTableError("");
        setApiRounds([]);
        setRoundsLoadedCollegeId("");
        setActiveRound(undefined);
        setActiveRoundId("");
        return;
      }

      const roundsUrl = `/api/v1/spot/viewer/colleges/${encodeURIComponent(activeViewerCollegeId)}/rounds`;
      const roundsRequestUrl = `${String(httpClient.defaults.baseURL ?? "").replace(/\/$/, "")}${roundsUrl}`;
      const roundsRequestStartedAt = Date.now();
      const timeoutLogTimer = window.setTimeout(() => {
        console.warn("[SpotRound] rounds request timeout warning (>10s)", {
          collegeId: activeViewerCollegeId,
          requestUrl: roundsRequestUrl,
          elapsedMs: Date.now() - roundsRequestStartedAt,
        });
      }, 10000);

      setLoadingRounds(true);
      console.log("[SpotRound] loadingRounds = true");
      setTableStatus("loading");
      setTableError("");
      setApiRounds([]);
      setRoundsLoadedCollegeId("");
      setActiveRound(undefined);
      setActiveRoundId("");

      try {
        // TEMP DEBUG — required logs for viewer switching flow.
        console.log("[SpotRound] rounds request started", {
          collegeId: activeViewerCollegeId,
          requestUrl: roundsRequestUrl,
        });

        const roundsResponse = await fetchViewerCollegeRounds(activeViewerCollegeId);
        // TEMP DEBUG
        console.log("[SpotRound] rounds request completed", {
          collegeId: activeViewerCollegeId,
          requestUrl: roundsRequestUrl,
          elapsedMs: Date.now() - roundsRequestStartedAt,
        });
        console.log("[SpotRound] rounds response", roundsResponse);

        const selectedRoundId = pickDefaultViewerRoundId(roundsResponse) ?? "";
        // TEMP DEBUG
        console.log("[SpotRound] selected roundId", selectedRoundId);

        const mappedRounds = sortMappedSpotRoundsAsc(
          roundsResponse.map((round) => mapViewerRoundToAdmissionRound(round)),
          "[SpotRound][Public]",
        );

        if (cancelled) {
          return;
        }

        setApiRounds(mappedRounds);
        setRoundsLoadedCollegeId(activeViewerCollegeId);
        setActiveRoundId(selectedRoundId);
        if (!selectedRoundId) {
          setTableStatus("success");
        }
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }
        setApiRounds([]);
        setRoundsLoadedCollegeId("");
        setActiveRoundId("");
        setActiveRound(undefined);
        setTableStatus("error");
        setTableError(
          error instanceof Error && error.message
            ? error.message
            : "Unable to load Spot Round table.",
        );
      } finally {
        window.clearTimeout(timeoutLogTimer);
        if (!cancelled) {
          setLoadingRounds(false);
          console.log("[SpotRound] loadingRounds = false");
        }
      }
    };

    void loadRounds();
    return () => {
      cancelled = true;
    };
  }, [activeViewerCollegeId, viewerCollegeReloadNonce]);

  React.useEffect(() => {
    if (!activeRoundId || !activeViewerCollegeId) {
      return;
    }

    // Enforce strict flow: spots API runs only after rounds API finished for selected college.
    if (roundsLoadedCollegeId !== activeViewerCollegeId) {
      return;
    }

    const numericRoundId = Number(activeRoundId);
    if (!Number.isFinite(numericRoundId)) {
      return;
    }

    // TEMP DEBUG — required logs for viewer switching flow.
    console.log("[SpotRound] spots API request", {
      roundId: numericRoundId,
      viewerCollegeId: activeViewerCollegeId || null,
    });

    void loadRoundSpots(activeRoundId);
  }, [activeRoundId, activeViewerCollegeId, roundsLoadedCollegeId, loadRoundSpots]);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  React.useEffect(() => {
    const { next, expiredKeys } = expireTickerHighlights(tickerHighlightMap, now);
    if (!expiredKeys.length) {
      return;
    }

    for (const key of expiredKeys) {
      console.log("[SpotRound] ticker highlight expired", {
        key,
        expiredAt: now,
        tickerRedStateActive: false,
      });
    }
    setTickerHighlightMap(next);
  }, [tickerHighlightMap, now]);

  React.useEffect(() => {
    saveSpotShortlistNames(shortlistedNames);
  }, [shortlistedNames]);

  React.useEffect(() => {
    if (apiRounds.length === 0) {
      return;
    }
    // TEMP DEBUG
    console.log(
      "[SpotRound][Public] rendered round order",
      apiRounds.map((round) => ({ id: round.id, name: round.name })),
    );
  }, [apiRounds]);

  const shortlistLabel = shortlistTickerStatus === "success" && shortlistTickerItems.length === 0
    ? "★ Your shortlisted colleges (none yet)"
    : "★ Your shortlisted colleges";

  const selectCollegeFromSeed = (id: string) => {
    const college = getCollege(data, id);
    if (!college) {
      return;
    }
    setActiveCollegeId(id);
  };

  const triggerViewerCollegeReload = (
    clickedCollegeId: string,
    source: "exporter" | "fallback",
  ) => {
    const previousSelectedCollegeId = activeViewerCollegeIdRef.current;
    const reloadTriggered = Boolean(clickedCollegeId);

    // TEMP DEBUG — required logs for ticker-driven viewer switching.
    console.log("[SpotRound] ticker click reload", {
      clickedCollegeId: clickedCollegeId || null,
      previousSelectedCollegeId: previousSelectedCollegeId || null,
      reloadTriggered,
      sameCollegeReselected:
        Boolean(clickedCollegeId)
        && clickedCollegeId === previousSelectedCollegeId,
      source,
    });

    // Clear previous rounds/table immediately; stale in-flight requests are cancelled via effect cleanup.
    setTableStatus("loading");
    setTableError("");
    setApiRounds([]);
    setActiveRound(undefined);
    setActiveRoundId("");
    setRoundsLoadedCollegeId("");

    setActiveViewerCollegeId(clickedCollegeId);
    if (reloadTriggered) {
      setViewerCollegeReloadNonce((nonce) => nonce + 1);
    }
  };

  const selectTickerCollege = (item: SpotTickerItem) => {
    console.log("[SpotRound] ticker clicked college", {
      collegeName: item.college_name,
      collegeCode: item.college_code,
    });

    const matchedExporter = findExporterCollegeByTickerItem(exporterColleges, item);
    if (matchedExporter) {
      setHeaderCollege(matchedExporter);
      logSelectedHeaderCollege("ticker select", matchedExporter);

      const resolvedViewerCollegeId = resolveSpotCollegeViewerId(matchedExporter);
      console.log("[SpotRound] resolved collegeId", resolvedViewerCollegeId);
      triggerViewerCollegeReload(resolvedViewerCollegeId ?? "", "exporter");

      const viewerId = resolvedViewerCollegeId;
      if (viewerId) {
        const seedMatch = colleges.find((college) => college.id === viewerId);
        if (seedMatch) {
          setActiveCollegeId(seedMatch.id);
          return;
        }
      }
    }

    const matched = findSeedCollegeByTickerItem(colleges, item);
    if (matched) {
      selectCollegeFromSeed(matched.id);
    }

    if (!matchedExporter) {
      const fallbackResolvedViewerCollegeId = resolveSpotCollegeViewerId(
        { id: null, name: item.college_name } as SpotCollegeListItem,
      );
      console.log("[SpotRound] resolved collegeId (fallback)", fallbackResolvedViewerCollegeId);
      triggerViewerCollegeReload(fallbackResolvedViewerCollegeId ?? "", "fallback");
    }
  };

  const toggleShortlistByName = (collegeName: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setShortlistedNames((prev) => {
      const index = prev.findIndex((name) => name.toLowerCase() === collegeName.toLowerCase());
      if (index === -1) {
        return [...prev, collegeName];
      }
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const toggleShortlistFromTicker = (item: SpotTickerItem, event: React.MouseEvent) => {
    toggleShortlistByName(item.college_name, event);
  };

  if (!activeCollege) {
    return (
      <section className="spot-round-page" id="page-spotrounds">
        <div className="section-header">
          <h2>Spot Round</h2>
        </div>
        <div className="sr2-empty">No Spot Round colleges available yet.</div>
      </section>
    );
  }

  const shortlistTargetName = displayCollegeName;
  const isHeaderShortlisted = Boolean(
    shortlistTargetName
    && shortlistedNames.some(
      (name) => name.toLowerCase() === shortlistTargetName.toLowerCase(),
    ),
  );

  return (
    <section className="spot-round-page" id="page-spotrounds">
      <div className="section-header">
        <h2>Spot Round</h2>
      </div>

      <TickerSection
        label="All Spot Round colleges"
        items={allTickerItems}
        status={allTickerStatus}
        errorMessage={allTickerError}
        emptyMessage="No Spot Round colleges available in the ticker yet."
        shortlistedNames={shortlistedNames}
        tickerHighlightMap={tickerHighlightMap}
        tickerUpdatedOnByCollege={tickerUpdatedOnByCollege}
        now={now}
        onSelect={selectTickerCollege}
        onToggleShortlist={toggleShortlistFromTicker}
      />

      <div className="sr2-ticker-gap" />

      <TickerSection
        label={shortlistLabel}
        items={shortlistTickerItems}
        status={shortlistTickerStatus}
        errorMessage={shortlistTickerError}
        emptyMessage="No shortlisted colleges in Spot Round yet. Shortlist colleges from the Colleges page to see them here."
        shortlistedNames={shortlistedNames}
        tickerHighlightMap={tickerHighlightMap}
        tickerUpdatedOnByCollege={tickerUpdatedOnByCollege}
        now={now}
        onSelect={selectTickerCollege}
        onToggleShortlist={toggleShortlistFromTicker}
      />

      <div className="sr2-college-head">
        <div className="sr2-college-logo">
          {displayCollegeLogoUrl ? (
            <img
              className="sr2-college-logo__img"
              src={displayCollegeLogoUrl}
              alt=""
              onLoad={() => {
                console.log("[SpotRound] image onLoad", {
                  selectedCollegeId,
                  imgSrc: displayCollegeLogoUrl,
                  headerCollegeLogoUrl: headerCollege?.logoUrl ?? null,
                });
              }}
              onError={(event) => {
                console.error("[SpotRound] image onError", {
                  selectedCollegeId,
                  imgSrc: displayCollegeLogoUrl,
                  headerCollegeLogoUrl: headerCollege?.logoUrl ?? null,
                  errorType: event.type,
                });
              }}
            />
          ) : (
            displayLogoInitial
          )}
        </div>
        <div>
          <div className="sr2-college-name">{displayCollegeName}</div>
          <div className="sr2-college-addr">{displayCollegeAddress}</div>
        </div>
        {lastUpdatedAt ? (
          <div className="last-updated">
            <div className="lu-col">
              <span className="lu-label">Last time refreshed</span>
              <span className="lu-abs">{formatAbs(lastUpdatedAt)}</span>
            </div>
          </div>
        ) : null}
        <Button
          type="button"
          label={isHeaderShortlisted ? "★ Shortlisted" : "☆ Shortlist"}
          className={`sr2-shortlist-btn${isHeaderShortlisted ? " on" : ""}`}
          disabled={!shortlistTargetName}
          onClick={() => {
            if (!shortlistTargetName) {
              return;
            }
            toggleShortlistByName(shortlistTargetName);
          }}
        />
      </div>

      <div className="rr-tab-panel active">
        <div className="sr2-round-chips">
          {loadingRounds && apiRounds.length === 0 ? (
            <div className="sr2-ticker-state">
              <ProgressSpinner style={{ width: "1.8rem", height: "1.8rem" }} strokeWidth="6" animationDuration=".9s" />
              <span>Loading rounds...</span>
            </div>
          ) : null}
          {tableStatus === "error" && apiRounds.length === 0 ? (
            <div className="sr2-ticker-state sr2-ticker-state--error">{tableError}</div>
          ) : null}
          {apiRounds.length === 0 && tableStatus === "success" ? (
            <span className="sr2-empty">No rounds yet.</span>
          ) : null}
          {apiRounds.map((round) => {
            const range = formatDateRange(round.dayStart, round.dayEnd);
            return (
              <button
                key={round.id}
                type="button"
                className={`sr2-round-chip${round.id === activeRoundId ? " active" : ""}`}
                onClick={() => setActiveRoundId(round.id)}
              >
                {round.name}
                {range ? ` · ${range}` : ""}
                &nbsp;
                <span className={`sr2-pill ${statusPillClass(round.status)}`}>{round.status}</span>
              </button>
            );
          })}
        </div>

        {tableStatus === "loading" && activeRoundId ? (
          <div className="sr2-ticker-state">
            <ProgressSpinner style={{ width: "1.8rem", height: "1.8rem" }} strokeWidth="6" animationDuration=".9s" />
            <span>Loading seat information...</span>
          </div>
        ) : null}
        {tableStatus === "error" && activeRoundId ? (
          <div className="sr2-ticker-state sr2-ticker-state--error">{tableError}</div>
        ) : null}
        {tableStatus === "success" ? (
          <SpotRoundSeatMatrix
            round={activeRound}
            highlightedCellKeys={highlightedCellKeys}
            highlightMetaByKey={highlightMetaByKey}
          />
        ) : null}
      </div>
    </section>
  );
}
